// src/features/treinos/components/ModoTreino.jsx
//
// VIEW da sessão ao vivo — lê TUDO do LiveWorkoutProvider (Parte B). Não possui
// timer/sessão próprios: o timer roda no provider e sobrevive à navegação.
// Abrir = openSession; sair/voltar = minimizeSession (NÃO encerra a sessão).
//
// Parte C: timer auto-start (provider) + botão pause/resume (sem zerar).
// Parte E: 3 cards de métrica (ícone→título→valor).
// Parte G: "Adicionar exercício" no topo E no rodapé.
// Parte H: tabela de séries pré-preenchida (dim) → acende ao concluir.
// Parte I: modal "Tem certeza…?" no Finalizar → tela Sessão finalizada.

import React, { useMemo, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import Offcanvas from "../../../components/common/SwipeableOffcanvas.jsx";
import BoxShadow from "../../../components/layout/BoxShadow.jsx";
import { VideoThumb, VideoModal } from "./VideoExercicio.jsx";
import {
  FaChevronLeft,
  FaChevronRight,
  FaPlus,
  FaPlay,
  FaPause,
  FaCheck,
  FaFireAlt,
  FaDumbbell,
  FaBolt,
} from "react-icons/fa";
import { FiChevronDown } from "react-icons/fi";
import ExerciseLibrary from "./treino-screens/ExerciseLibrary.jsx";
import SessaoFinalizada from "./SessaoFinalizada.jsx";
import TreinoDiaBlocos from "./TreinoDiaBlocos.jsx";
import { useLiveWorkout, useLiveWorkoutTimer } from "../../../contexts/LiveWorkoutContext.jsx";
import { useExerciseMedia } from "../hooks/useExerciseMedia.js";
import Style from "../../../styles/features/treinos/ModoTreino.module.css";

function formatTimer(secs) {
  const m = Math.floor(secs / 60).toString().padStart(2, "0");
  const s = (secs % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

// só dígitos (reps/descanso/rpe); carga aceita 1 separador decimal.
function sanitize(field, v) {
  if (field === "carga") return String(v).replace(/[^0-9.,]/g, "").replace(",", ".");
  return String(v).replace(/[^0-9]/g, "");
}

// Célula de input da tabela de séries. DEFINIDA NO ESCOPO DO MÓDULO de
// propósito: se ficasse declarada dentro de SeriesTable, cada re-render (a cada
// tecla, via onEdit→estado) criaria uma função nova → React remonta o <input> e
// o foco "pula" após 1 dígito. No módulo o tipo é estável → sem remontagem.
function Cell({ ex, rowIdx, field, placeholder, disabled, onEdit }) {
  return (
    <input
      className={Style.cellInput}
      type="tel"
      inputMode="numeric"
      value={ex.rows[rowIdx][field] ?? ""}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onEdit(ex._id, rowIdx, field, sanitize(field, e.target.value))}
      onFocus={(e) => e.target.select()}
    />
  );
}

// ── Tabela de séries (Parte H) ─────────────────────────────────────────────
function SeriesTable({ ex, onEdit, onToggle, onAddSet }) {
  // RPE = coluna OPCIONAL: só renderiza quando há RPE alvo/dado. // decisão
  const showRpe = ex.PLAN?.rpe != null || ex.rows.some((r) => r.rpe !== "" && r.rpe != null);
  const cols = `34px 1fr 1fr 1fr ${showRpe ? "0.7fr " : ""}44px`;

  return (
    <div className={Style.table}>
      <div className={Style.tHead} style={{ gridTemplateColumns: cols }}>
        <span>Série</span>
        <span>Reps</span>
        <span>Carga</span>
        <span>Desc.</span>
        {showRpe && <span>RPE</span>}
        <span className={Style.tStatusHead}>✓</span>
      </div>

      {ex.rows.map((r, idx) => (
        <div
          key={idx}
          className={`${Style.tRow} ${r.done ? Style.tRowDone : Style.tRowDim}`}
          style={{ gridTemplateColumns: cols }}
        >
          <span className={Style.serieNum}>{idx + 1}{r.extra ? "＋" : ""}</span>
          <Cell ex={ex} onEdit={onEdit} rowIdx={idx} field="reps" placeholder="–" />
          <Cell ex={ex} onEdit={onEdit} rowIdx={idx} field="carga" placeholder="–" />
          <Cell ex={ex} onEdit={onEdit} rowIdx={idx} field="descanso" placeholder="–" />
          {showRpe && <Cell ex={ex} onEdit={onEdit} rowIdx={idx} field="rpe" placeholder="–" />}
          <button
            type="button"
            className={`${Style.statusBtn} ${r.done ? Style.statusDone : ""}`}
            onClick={() => onToggle(ex, idx)}
            aria-label={r.done ? "Desfazer série" : "Concluir série"}
            aria-pressed={r.done}
          >
            <FaCheck size={12} />
          </button>
        </div>
      ))}

      <button type="button" className={Style.addSetBtn} onClick={() => onAddSet(ex._id)}>
        <FaPlus size={10} /> série extra
      </button>
    </div>
  );
}

// ── Card de exercício ──────────────────────────────────────────────────────
function ExercicioCard({ ex, media, expanded, isCurrent, onToggle, onVideoClick, onEdit, onToggleSet, onAddSet }) {
  const plan = ex.PLAN || {};
  const alvoTexto = [
    plan.series && `${plan.series} séries`,
    plan.reps && `${plan.reps} reps`,
    plan.descanso && `descanso ${plan.descanso}s`,
  ].filter(Boolean).join(" · ");

  const doneCount = ex.rows.filter((r) => r.done).length;
  const countText = `${doneCount}/${ex.rows.length} séries`;

  return (
    <BoxShadow className={`${Style.exCard} ${isCurrent ? Style.exCardAtual : ""}`}>
      <div
        className={Style.exCardHeader}
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onToggle(); }}
      >
        <span onClick={(e) => e.stopPropagation()}>
          <VideoThumb url={media?.url} thumbnail={media?.thumbnail} onClick={media?.url ? onVideoClick : undefined} width={72} height={52} />
        </span>
        <div className={Style.exCardInfo}>
          <span className={Style.exNome}>{ex.NOME}</span>
          {ex.MUSCLE_GROUP && <span className={Style.exMuscle}>{ex.MUSCLE_GROUP}</span>}
          {alvoTexto && <span className={Style.exAlvo}>{alvoTexto}</span>}
        </div>
        <div className={Style.exCardRight}>
          {isCurrent ? <span className={Style.atualBadge}>Atual</span> : <span className={Style.exCount}>{countText}</span>}
          <FiChevronDown className={`${Style.exChev} ${expanded ? Style.exChevOpen : ""}`} />
        </div>
      </div>

      {expanded && (
        <SeriesTable ex={ex} onEdit={onEdit} onToggle={onToggleSet} onAddSet={onAddSet} />
      )}
    </BoxShadow>
  );
}

// ── ModoTreino ─────────────────────────────────────────────────────────────
export default function ModoTreino() {
  const {
    session, exState, open, phase, metrics, currentEx,
    minimizeSession, finishSession, abandonSession, closeSession,
    pauseTimer, resumeTimer, updateRow, registerSet, addSet, addExercise,
  } = useLiveWorkout();
  // Cronômetro num contexto à parte (re-render por segundo isolado aqui).
  const { elapsed, running } = useLiveWorkoutTimer();

  const getMedia = useExerciseMedia(exState);
  const [expandedId, setExpandedId] = useState(null);
  const [showLib, setShowLib] = useState(false);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [confirmAbandon, setConfirmAbandon] = useState(false);
  const [video, setVideo] = useState(null);
  const [rest, setRest] = useState({ open: false, secs: 0 });
  const restRef = useRef(null);

  const isSummary = phase === "finished" || phase === "abandoned";
  const show = open && phase !== "idle";

  // Dia em blocos (funcional do personal): a sessão carrega o doc cru do dia em
  // raw.__personalDia. Quando for "blocks", a área central troca a listagem em
  // cards de exercício pela MESMA visualização de blocos do detalhe (TreinoDiaBlocos).
  const diaBlocos = session?.raw?.__personalDia || null;
  const isFuncional =
    !!session?.raw?.__isFuncional ||
    (diaBlocos?.workout_mode || "").toString().toLowerCase() === "blocks";

  const currentExId = currentEx?._id ?? null;
  const effectiveExpanded = expandedId ?? currentExId;

  // ── Descanso (countdown ao concluir série) — UI local ─────────────────────
  const stopRest = () => { if (restRef.current) { clearInterval(restRef.current); restRef.current = null; } };
  const startRest = (secs) => {
    stopRest();
    const inicial = Math.max(0, Number(secs) || 0);
    setRest({ open: inicial > 0, secs: inicial });
    if (inicial > 0) {
      restRef.current = setInterval(() => {
        setRest((r) => {
          if (r.secs <= 1) { stopRest(); return { ...r, secs: 0 }; }
          return { ...r, secs: r.secs - 1 };
        });
      }, 1000);
    }
  };
  const closeRest = () => { stopRest(); setRest({ open: false, secs: 0 }); };
  const adjustRest = (d) => setRest((r) => ({ ...r, secs: Math.max(0, r.secs + d) }));

  // Concluir série: registra no provider e dispara descanso (se virou "done").
  const handleToggleSet = (ex, idx) => {
    const willComplete = !ex.rows[idx].done;
    registerSet(ex._id, idx);
    if (willComplete) {
      const descanso = parseInt(ex.rows[idx].descanso, 10) || parseInt(ex.PLAN?.descanso, 10) || 60;
      startRest(descanso);
    }
  };

  const handleAddFromLib = (libRow) => { addExercise(libRow); setShowLib(false); };

  const onHideShell = () => { if (isSummary) closeSession(); else minimizeSession(); };

  if (!show) return null;

  // ── Tela de resumo (Parte I/J/K) ──────────────────────────────────────────
  if (isSummary) {
    return (
      <Offcanvas show onHide={onHideShell} placement="end" className={Style.offcanvas}>
        <Offcanvas.Body className={Style.summaryBody}>
          <SessaoFinalizada
            onArmAppear={() => { /* hook SFX #1 — áudio entra depois */ }}
            onMusclePop={() => { /* hook SFX #2 — áudio entra depois */ }}
          />
        </Offcanvas.Body>
      </Offcanvas>
    );
  }

  // ── Tela principal ────────────────────────────────────────────────────────
  return (
    <>
      <Offcanvas show onHide={onHideShell} placement="end" className={Style.offcanvas}>
        <Offcanvas.Header className={Style.header}>
          <Button variant="link" onClick={minimizeSession} className={Style.backBtn} aria-label="Minimizar (continua rodando)">
            <FaChevronLeft size={20} />
          </Button>
          <span className={Style.headerTitle}>{session?.nomeTreino || session?.diaTreino || "Treino"}</span>
          <span className={Style.statusBadge}>● Em andamento</span>
        </Offcanvas.Header>

        <Offcanvas.Body className={Style.body}>
          {/* Cronômetro — auto-start no provider; botão pausa/retoma (sem zerar) */}
          <div className={Style.timerBlock}>
            <div className={Style.timerTexts}>
              <span className={Style.timerLabel}>Tempo decorrido</span>
              <div className={Style.timerBig}>{formatTimer(elapsed)}</div>
            </div>
            <button
              className={Style.timerCtl}
              onClick={() => (running ? pauseTimer() : resumeTimer())}
              title={running ? "Pausar" : "Retomar"}
              aria-label={running ? "Pausar cronômetro" : "Retomar cronômetro"}
            >
              {running ? <FaPause size={14} /> : <FaPlay size={14} />}
            </button>
          </div>

          {/* 3 cards de métrica (Parte E): ícone → título → valor+unidade */}
          <div className={Style.metricCards}>
            <div className={Style.metricCard}>
              <div className={Style.metricTop}>
                <span className={Style.metricIcon}><FaFireAlt size={13} /></span>
                <span className={Style.metricTitle}>Calorias</span>
              </div>
              <div className={Style.metricValue}>
                <span className={Style.metricNum}>{metrics.caloriasLive}</span>
                <span className={Style.metricUnit}>kcal</span>
              </div>
            </div>
            <div className={Style.metricCard}>
              <div className={Style.metricTop}>
                <span className={Style.metricIcon}><FaDumbbell size={13} /></span>
                <span className={Style.metricTitle}>Volume total</span>
              </div>
              <div className={Style.metricValue}>
                <span className={Style.metricNum}>{metrics.volumeTotal.toLocaleString("pt-BR")}</span>
                <span className={Style.metricUnit}>kg</span>
              </div>
            </div>
            <div className={Style.metricCard}>
              <div className={Style.metricTop}>
                <span className={Style.metricIcon}><FaBolt size={13} /></span>
                <span className={Style.metricTitle}>XP ganho</span>
              </div>
              <div className={Style.metricValue}>
                <span className={Style.metricNum}>+{metrics.xpLive}</span>
                <span className={Style.metricUnit}>XP</span>
              </div>
            </div>
          </div>

          {/* Progresso */}
          <div className={Style.progressWrap}>
            <div className={Style.progressHead}>
              <span>Progresso do treino</span>
              <span className={Style.progressLabel}>{metrics.exDone} / {metrics.exTotal} exercícios</span>
            </div>
            <div className={Style.progressTrack}>
              <div className={Style.progressFill} style={{ width: `${Math.round(metrics.progresso * 100)}%` }} />
            </div>
          </div>

          <div className={Style.scrollArea}>
            {isFuncional ? (
              /* Dia em blocos: só visualização (mesma do detalhe), sem cards de
                 série e sem "Adicionar exercício" (modo simplificado por enquanto). */
              <TreinoDiaBlocos
                dia={diaBlocos}
                onOpenVideo={(url, nome, url2 = "") => setVideo({ url, url2, nome })}
              />
            ) : (
              <>
                {/* Parte G: adicionar exercício no TOPO */}
                <button type="button" className={Style.addExerciseBtn} onClick={() => setShowLib(true)}>
                  <FaPlus size={12} /> Adicionar exercício
                </button>

                {exState.map((ex) => {
                  const media = getMedia(ex);
                  return (
                    <ExercicioCard
                      key={ex._id}
                      ex={ex}
                      media={media}
                      expanded={effectiveExpanded === ex._id}
                      isCurrent={currentExId === ex._id}
                      onToggle={() => setExpandedId(effectiveExpanded === ex._id ? "__collapsed__" : ex._id)}
                      onVideoClick={() => setVideo({ url: media.url, url2: media.url2, nome: ex.NOME })}
                      onEdit={updateRow}
                      onToggleSet={handleToggleSet}
                      onAddSet={addSet}
                    />
                  );
                })}

                {/* Parte G: adicionar exercício no RODAPÉ (mantido) */}
                <button type="button" className={Style.addExerciseBtn} onClick={() => setShowLib(true)}>
                  <FaPlus size={12} /> Adicionar exercício
                </button>
              </>
            )}
          </div>

          <div className={Style.stickyFooter}>
            <Button variant="outline-secondary" className={Style.btnAbandonar} onClick={() => setConfirmAbandon(true)}>
              Abandonar
            </Button>
            <Button variant="primary" className={Style.btnFinalizar} onClick={() => setConfirmFinish(true)}>
              Finalizar treino <FaChevronRight size={11} />
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Parte I — modal de confirmação do Finalizar */}
      <Modal show={confirmFinish} onHide={() => setConfirmFinish(false)} centered className={Style.confirmModal}>
        <Modal.Body className={Style.confirmBody}>
          <h5>Tem certeza que quer finalizar?</h5>
          <p>Você concluiu {metrics.completedSets} de {metrics.plannedSets} séries planejadas.</p>
          <div className={Style.confirmBtns}>
            <Button variant="outline-secondary" className={Style.confirmGhost} onClick={() => setConfirmFinish(false)}>
              Ainda não
            </Button>
            <Button variant="primary" className={Style.confirmGold} onClick={() => { setConfirmFinish(false); finishSession(); }}>
              Finalizar
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Confirmação do Abandonar — séries já registradas são salvas, sem check-in */}
      <Modal show={confirmAbandon} onHide={() => setConfirmAbandon(false)} centered className={Style.confirmModal}>
        <Modal.Body className={Style.confirmBody}>
          <h5>Tem certeza que quer abandonar?</h5>
          <p>O treino será encerrado sem check-in. As séries já registradas continuam salvas.</p>
          <div className={Style.confirmBtns}>
            <Button variant="outline-secondary" className={Style.confirmGhost} onClick={() => setConfirmAbandon(false)}>
              Continuar treino
            </Button>
            <Button variant="primary" className={Style.confirmGold} onClick={() => { setConfirmAbandon(false); abandonSession(); }}>
              Abandonar
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <VideoModal show={!!video} onHide={() => setVideo(null)} url={video?.url} url2={video?.url2} nome={video?.nome} />

      {/* Descanso entre séries */}
      <Offcanvas show={rest.open} onHide={closeRest} placement="bottom" className={Style.restOffcanvas} backdrop={false} scroll style={{ height: "auto" }}>
        <Offcanvas.Body className={Style.restBody}>
          <span className={Style.restLabel}>Descanso</span>
          <div className={Style.restTimer}>{formatTimer(rest.secs)}</div>
          <div className={Style.restControls}>
            <Button className={Style.restAdjust} onClick={() => adjustRest(-15)}>−15s</Button>
            <Button className={Style.restSkip} onClick={closeRest}>Pular</Button>
            <Button className={Style.restAdjust} onClick={() => adjustRest(15)}>+15s</Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <ExerciseLibrary show={showLib} onHide={() => setShowLib(false)} onAdd={handleAddFromLib} />
    </>
  );
}
